// client/src/components/secure/ReconciliationPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { printWorksheet } from '../../utils/escrowPrint';
import DataList from '../shared/DataList';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const ReconciliationPanel = ({ accountId, onReconciled }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [statementDate, setStatementDate] = useState('');
    const [statementBalance, setStatementBalance] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const apiClient = useApiClient();

    const fetchData = useCallback(() => {
        setIsLoading(true);
        apiClient.get(`/api/escrow/accounts/${accountId}/reconciliation`)
            .then(setData)
            .catch(err => setError(err.message || 'Failed to load reconciliation data.'))
            .finally(() => setIsLoading(false));
    }, [accountId, apiClient]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const me = getCurrentUser();
    const [signing, setSigning] = useState(null);
    const sign = async () => {
        setError('');
        try {
            await apiClient.post(`/api/escrow-rules/reconciliations/${signing.reconciliation_id}/sign`,
                { explanation: signing.explanation, corrective_action: signing.corrective_action });
            setSigning(null); fetchData();
        } catch (err) { setError(err.message || 'Could not sign.'); }
    };

    const print = async (reconciliationId) => {
        try { printWorksheet(await apiClient.get(`/api/escrow/reconciliation-worksheet/${reconciliationId}`)); }
        catch (err) { setError(err.message || 'Could not build the worksheet.'); }
    };

    const toggleEntry = (entryId) => {
        setSelectedIds(prev => prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]);
    };

    const selectedNet = (data?.unclearedEntries || [])
        .filter(e => selectedIds.includes(e.entry_id))
        .reduce((sum, e) => sum + (e.entry_type === 'Deposit' ? Number(e.amount) : -Number(e.amount)), 0);

    const projectedClearedBalance = Number(data?.clearedBalance || 0) + selectedNet;
    const projectedDifference = statementBalance !== '' ? Number(statementBalance) - projectedClearedBalance : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setResult(null);
        try {
            const newReconciliation = await apiClient.post(`/api/escrow/accounts/${accountId}/reconciliation`, {
                statement_date: statementDate,
                statement_ending_balance: statementBalance,
                cleared_entry_ids: selectedIds,
                notes,
            });
            setResult(newReconciliation);
            setSelectedIds([]);
            setStatementDate('');
            setStatementBalance('');
            setNotes('');
            fetchData();
            if (onReconciled) onReconciled();
        } catch (err) {
            setError(err.message || 'Failed to post reconciliation.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <div className="bg-white p-8 rounded-lg shadow-lg mt-8">Loading reconciliation data...</div>;

    return (
        <div className="bg-white p-8 rounded-lg shadow-lg mt-8">
            <h2 className="text-2xl font-bold mb-4">Bank Reconciliation</h2>

            {result && (
                <div className={`p-4 rounded-md mb-6 ${result.status === 'Balanced' ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
                    <p className="font-semibold">{result.status === 'Balanced' ? 'Reconciled — bank, journal and ledgers agree.' : 'Reconciliation posted with a discrepancy.'}</p>
                    <div className="text-sm mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                        <span>Bank, adjusted: {formatCurrency(result.reconciled_bank_balance)}</span>
                        <span>Journal: {formatCurrency(result.journal_balance)}</span>
                        <span>Ledgers: {formatCurrency(result.reconciled_ledger_balance)}</span>
                    </div>
                    <p className="text-xs mt-2">
                        {result.deposits_in_transit_count || 0} deposit(s) in transit and {result.outstanding_checks_count || 0} outstanding check(s) listed on the worksheet.
                        {Math.abs(Number(result.bank_vs_journal || 0)) >= 0.01 && ` Bank and journal differ by ${formatCurrency(result.bank_vs_journal)}.`}
                        {Math.abs(Number(result.journal_vs_ledger || 0)) >= 0.01 && ` Journal and ledgers differ by ${formatCurrency(result.journal_vs_ledger)} — usually an entry on no ledger.`}
                    </p>
                    <button onClick={() => print(result.reconciliation_id)} className="mt-3 px-3 py-1 text-sm bg-slate-800 text-white rounded">Print worksheet</button>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Statement Date</label>
                        <input type="date" value={statementDate} onChange={e => setStatementDate(e.target.value)} required className="mt-1 p-2 border rounded w-full" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Statement Ending Balance</label>
                        <input type="number" step="0.01" value={statementBalance} onChange={e => setStatementBalance(e.target.value)} required className="mt-1 p-2 border rounded w-full" />
                    </div>
                </div>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="p-2 border rounded w-full" />

                <div>
                    <h3 className="text-lg font-semibold mb-2">Uncleared Entries</h3>
                    <p className="text-sm text-slate-500 mb-3">Check off every entry that appears on this bank statement. Anything left unchecked and dated on or before the statement goes on the worksheet as a deposit in transit or an outstanding check.</p>
                    <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2"></th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Payor/Payee</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Deposit</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Disbursement</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(data?.unclearedEntries || []).map(entry => (
                                    <tr key={entry.entry_id}>
                                        <td className="px-4 py-2"><input type="checkbox" checked={selectedIds.includes(entry.entry_id)} onChange={() => toggleEntry(entry.entry_id)} /></td>
                                        <td className="px-4 py-2 text-sm">{new Date(entry.entry_date).toLocaleDateString()}</td>
                                        <td className="px-4 py-2 text-sm">{entry.payee_or_payor}</td>
                                        <td className="px-4 py-2 text-sm text-right text-green-600">{entry.entry_type === 'Deposit' ? formatCurrency(entry.amount) : ''}</td>
                                        <td className="px-4 py-2 text-sm text-right text-red-600">{entry.entry_type === 'Disbursement' ? formatCurrency(entry.amount) : ''}</td>
                                    </tr>
                                ))}
                                {(data?.unclearedEntries || []).length === 0 && (
                                    <tr><td colSpan="5" className="text-center py-6 text-slate-500">No uncleared entries — everything is already cleared.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-md text-sm space-y-1">
                    <div className="flex justify-between"><span>Previously cleared balance:</span><span>{formatCurrency(data?.clearedBalance || 0)}</span></div>
                    <div className="flex justify-between"><span>Newly selected net:</span><span>{formatCurrency(selectedNet)}</span></div>
                    <div className="flex justify-between font-semibold"><span>Projected cleared balance:</span><span>{formatCurrency(projectedClearedBalance)}</span></div>
                    {projectedDifference !== null && (
                        <div className={`flex justify-between font-semibold ${Math.abs(projectedDifference) < 0.01 ? 'text-green-700' : 'text-yellow-700'}`}>
                            <span>Projected difference vs. statement:</span><span>{formatCurrency(projectedDifference)}</span>
                        </div>
                    )}
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}
                <div className="flex justify-end">
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                        {isSubmitting ? 'Posting...' : 'Post Reconciliation'}
                    </button>
                </div>
            </form>

            <h3 className="text-lg font-semibold mb-2">Reconciliation History</h3>
            <DataList rows={data?.history || []} rowKey="reconciliation_id" csvName="escrow-reconciliations" defaultSort={{ key: 'statement_date', dir: 'desc' }}
                empty="No reconciliations posted yet." filters={[{ key: 'status', label: 'Status' }, { key: 'signed', label: 'Signed' }]}
                columns={[
                    { key: 'statement_date', label: 'Statement Date', type: 'date', value: (r) => (r.statement_date ? String(r.statement_date).slice(0, 10) : null) },
                    { key: 'statement_ending_balance', label: 'Statement Balance', type: 'number', render: (r) => formatCurrency(r.statement_ending_balance) },
                    { key: 'difference', label: 'Difference', type: 'number', render: (r) => formatCurrency(r.difference) },
                    { key: 'status', label: 'Status', render: (r) => <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${r.status === 'Balanced' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{r.status}</span> },
                    { key: 'reconciled_by_name', label: 'By', value: (r) => r.reconciled_by_name || 'N/A' },
                    { key: 'signed', label: 'Broker sign-off', value: (r) => (r.broker_signed_at ? `Signed ${String(r.broker_signed_at).slice(0, 10)}` : 'Not signed'), sortValue: (r) => r.broker_signed_at || null,
                        render: (r) => (r.broker_signed_at
                            ? <span className="text-emerald-700" title={r.discrepancy_explanation || ''}>Signed {new Date(r.broker_signed_at).toLocaleDateString()}</span>
                            : me?.isManagingBroker ? <button onClick={() => setSigning({ ...r, explanation: r.discrepancy_explanation || '', corrective_action: r.corrective_action || '' })} className="text-emerald-700 hover:underline">Review &amp; sign</button> : <span className="text-slate-400">Not signed</span>) },
                    { key: 'print', label: '', sortable: false, csv: false, search: false, render: (r) => <button onClick={() => print(r.reconciliation_id)} className="text-indigo-600 hover:underline whitespace-nowrap">Print worksheet</button> },
                ]} />
            {signing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                        <h3 className="text-lg font-bold mb-1">Review and sign — statement of {new Date(signing.statement_date).toLocaleDateString()}</h3>
                        <p className="text-sm text-slate-600 mb-3">{signing.status === 'Balanced' ? 'Bank, journal and ledgers agree.' : `Difference of ${formatCurrency(signing.difference)} — explain it and record the corrective action before signing.`}</p>
                        <label className="block text-sm mb-2">Explanation of any difference
                            <textarea rows={3} value={signing.explanation} onChange={(e) => setSigning((x) => ({ ...x, explanation: e.target.value }))} className="block w-full border rounded p-2" /></label>
                        <label className="block text-sm mb-2">Corrective action taken
                            <textarea rows={2} value={signing.corrective_action} onChange={(e) => setSigning((x) => ({ ...x, corrective_action: e.target.value }))} className="block w-full border rounded p-2" /></label>
                        <p className="text-xs text-slate-500 mb-3">Signing records you as the broker who reviewed this reconciliation, with today's date.</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setSigning(null)} className="px-3 py-2 bg-gray-200 rounded">Cancel</button>
                            <button onClick={sign} className="px-3 py-2 bg-emerald-600 text-white rounded">Sign and date</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReconciliationPanel;
